import React from 'react';

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
const mod = isMac ? 'Cmd' : 'Ctrl';

const SHORTCUT_SECTIONS = [
  {
    title: 'Execution',
    shortcuts: [
      { keys: 'Shift+Enter', desc: 'Run cell and move to next' },
      { keys: `${mod}+Enter`, desc: 'Run cell in place' },
      { keys: 'Alt+Enter', desc: 'Run cell and insert below' },
    ],
  },
  {
    title: 'Command Mode (press Escape first)',
    shortcuts: [
      { keys: 'Enter', desc: 'Enter edit mode' },
      { keys: 'A', desc: 'Insert cell above' },
      { keys: 'B', desc: 'Insert cell below' },
      { keys: 'D D', desc: 'Delete cell' },
      { keys: 'Z', desc: 'Undo cell delete' },
      { keys: 'M', desc: 'Change to Markdown' },
      { keys: 'Y', desc: 'Change to Code' },
      { keys: 'X', desc: 'Cut cell' },
      { keys: 'C', desc: 'Copy cell' },
      { keys: 'V', desc: 'Paste cell below' },
      { keys: 'O', desc: 'Toggle output' },
      { keys: 'L', desc: 'Toggle line numbers' },
      { keys: '1-6', desc: 'Set markdown heading level' },
      { keys: 'Shift+M', desc: 'Merge selected cells' },
      { keys: 'H', desc: 'Show this shortcuts reference' },
      { keys: 'Up/Down', desc: 'Navigate cells' },
      { keys: 'Shift+Up/Down', desc: 'Multi-select cells' },
    ],
  },
  {
    title: 'Edit Mode',
    shortcuts: [
      { keys: 'Shift+Tab', desc: 'Show docstring tooltip' },
      { keys: 'Tab', desc: 'Code completion / indent' },
      { keys: `${mod}+/`, desc: 'Toggle comment' },
      { keys: `${mod}+D`, desc: 'Select next occurrence' },
      { keys: `${mod}+F`, desc: 'Find in cell' },
      { keys: `${mod}+L`, desc: 'Ask AI about code' },
      { keys: `${mod}+Shift+-`, desc: 'Split cell at cursor' },
      { keys: 'Escape', desc: 'Enter command mode' },
    ],
  },
  {
    title: 'Global',
    shortcuts: [
      { keys: `${mod}+S`, desc: 'Save notebook' },
      { keys: `${mod}+Shift+P`, desc: 'Command palette' },
      { keys: `${mod}+B`, desc: 'Toggle left panel' },
      { keys: `${mod}+J`, desc: 'Toggle right panel' },
    ],
  },
];

export const ShortcutsModal: React.FC<ShortcutsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl max-h-[80vh] bg-gray-900 border border-gray-700 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <h2 className="text-lg font-semibold text-white">Keyboard Shortcuts</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {SHORTCUT_SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">{section.title}</h3>
              <div className="space-y-1">
                {section.shortcuts.map((s) => (
                  <div key={s.keys} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-800/50 transition-colors">
                    <span className="text-sm text-gray-300">{s.desc}</span>
                    <kbd className="text-xs font-mono text-gray-400 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                      {s.keys}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
